import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Paper, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Divider, TableContainer,
  MenuItem, Tabs, Tab,
} from '@mui/material';
import { Search, Edit, Save, ContactMail, AccountBalanceWallet, Business, PhoneForwarded } from '@mui/icons-material';
import api from '../../services/api';
import { CarrierContactsPanel } from './CarrierContactsPanel';

interface Row {
  id: string; name: string; company_name?: string; code?: string; status: string;
  billing_type?: string; currency?: string;
  balance: number; reserved_balance: number; credit_limit: number;
  contact_email?: string; contact_phone?: string;
  billing_email?: string; billing_contact_name?: string; billing_phone?: string;
  payment_terms?: string; tax_id?: string; billing_period?: string;
  technical_contact_name?: string; technical_contact_email?: string;
  rate_change_email?: string; cc_email?: string; address?: any;
  rate_plan?: string; open_invoices: number; open_amount: number;
  lifetime_paid: number; last_invoice_at?: string;
}

const money = (n: number, c?: string) =>
  (n < 0 ? '-' : '') + (c || 'USD') + ' ' + Math.abs(Number(n) || 0).toFixed(2);

const CustomerContacts: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/billing-contacts')
      .then(({ data }) => setRows(data || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.name, r.company_name, r.code, r.billing_email, r.contact_email, r.rate_plan]
        .some((v) => (v || '').toLowerCase().includes(s)));
  }, [rows, q]);

  const totalReceivable = rows.reduce((a, r) => a + (r.open_amount || 0), 0);
  const negatives = rows.filter((r) => (r.balance || 0) < 0).length;
  const set = (k: keyof Row, v: any) => setEdit((e) => (e ? { ...e, [k]: v } : e));

  const save = async () => {
    if (!edit) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`/billing-contacts/${edit.id}`, {
        billing_email: edit.billing_email, billing_contact_name: edit.billing_contact_name,
        billing_phone: edit.billing_phone, payment_terms: edit.payment_terms, tax_id: edit.tax_id,
        billing_period: edit.billing_period,
        technical_contact_name: edit.technical_contact_name, technical_contact_email: edit.technical_contact_email,
        rate_change_email: edit.rate_change_email, cc_email: edit.cc_email,
      });
      setSuccess(`Saved contacts for ${edit.name}.`);
      setEdit(null); load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}><Card variant="outlined"><CardContent>
          <Typography variant="overline" color="text.secondary">Customers</Typography>
          <Typography variant="h4">{rows.length}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} sm={4}><Card variant="outlined"><CardContent>
          <Typography variant="overline" color="text.secondary">Open Receivables</Typography>
          <Typography variant="h4">{money(totalReceivable)}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} sm={4}><Card variant="outlined"><CardContent>
          <Typography variant="overline" color="text.secondary">Negative Balances</Typography>
          <Typography variant="h4" color={negatives ? 'error' : 'inherit'}>{negatives}</Typography></CardContent></Card></Grid>
      </Grid>

      <TextField size="small" placeholder="Search customer, code, email, plan…" value={q}
        onChange={(e) => setQ(e.target.value)} sx={{ mb: 2, width: 360 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell><TableCell>Period</TableCell>
                <TableCell>Billing Email</TableCell><TableCell>Technical Email</TableCell>
                <TableCell>Rate-change Email</TableCell><TableCell>CC Email</TableCell>
                <TableCell align="right">Balance</TableCell><TableCell>Rate Plan</TableCell>
                <TableCell align="center">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell><b>{r.name}</b>{r.company_name ? <div style={{ fontSize: 12, color: '#888' }}>{r.company_name}</div> : null}</TableCell>
                  <TableCell><Chip size="small" color={r.billing_period === 'weekly' ? 'primary' : 'default'} label={r.billing_period || '—'} /></TableCell>
                  <TableCell>{r.billing_email || <i style={{ color: '#c00' }}>not set</i>}</TableCell>
                  <TableCell>{r.technical_contact_email || '—'}</TableCell>
                  <TableCell>{r.rate_change_email || '—'}</TableCell>
                  <TableCell>{r.cc_email || '—'}</TableCell>
                  <TableCell align="right" style={{ color: r.balance < 0 ? '#c00' : undefined, fontWeight: 600 }}>{money(r.balance, r.currency)}</TableCell>
                  <TableCell>{r.rate_plan || '—'}</TableCell>
                  <TableCell align="center"><Button size="small" startIcon={<Edit />} onClick={() => setEdit({ ...r })}>Edit</Button></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={9}><i>No customers match.</i></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        {edit && (<>
          <DialogTitle>Contacts — {edit.name}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccountBalanceWallet fontSize="small" /> Billing summary
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                ['Balance', money(edit.balance, edit.currency)],
                ['Credit limit', money(edit.credit_limit, edit.currency)],
                ['Billing type', edit.billing_type || '—'],
                ['Currency', edit.currency || '—'],
                ['Rate plan', edit.rate_plan || '—'],
                ['Open invoices', `${edit.open_invoices} (${money(edit.open_amount, edit.currency)})`],
                ['Lifetime paid', money(edit.lifetime_paid, edit.currency)],
                ['Account email', edit.contact_email || '—'],
                ['Status', edit.status],
              ].map(([k, v]) => (
                <Grid item xs={6} sm={4} key={k as string}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" fontWeight={600}>{v}</Typography>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Email" value={edit.billing_email || ''} onChange={(e) => set('billing_email', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Contact Name" value={edit.billing_contact_name || ''} onChange={(e) => set('billing_contact_name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Phone" value={edit.billing_phone || ''} onChange={(e) => set('billing_phone', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="CC (Carbon Copy) Email" value={edit.cc_email || ''} onChange={(e) => set('cc_email', e.target.value)} helperText="Cc'd on billing/notification emails" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Payment Terms (e.g. Net 30, Prepaid)" value={edit.payment_terms || ''} onChange={(e) => set('payment_terms', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select size="small" label="Billing Period" value={edit.billing_period || 'monthly'} onChange={(e) => set('billing_period', e.target.value)}>
                  <MenuItem value="weekly">Weekly</MenuItem><MenuItem value="biweekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem><MenuItem value="quarterly">Quarterly</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Tax ID / TRN / VAT" value={edit.tax_id || ''} onChange={(e) => set('tax_id', e.target.value)} /></Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Technical &amp; notifications</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Technical Contact Name" value={edit.technical_contact_name || ''} onChange={(e) => set('technical_contact_name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Technical Contact Email" value={edit.technical_contact_email || ''} onChange={(e) => set('technical_contact_email', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Rate Change Notification Email" value={edit.rate_change_email || ''} onChange={(e) => set('rate_change_email', e.target.value)} helperText="Receives rate-update alerts" /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEdit(null)}>Cancel</Button>
            <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={save}>Save</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </Box>
  );
};

export const BillingContactsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ContactMail color="primary" />
        <Typography variant="h4" fontWeight={700}>Customer / Carrier Contacts</Typography>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<Business />} iconPosition="start" label="Customers" />
        <Tab icon={<PhoneForwarded />} iconPosition="start" label="Carriers" />
      </Tabs>
      {tab === 0 && <CustomerContacts />}
      {tab === 1 && <CarrierContactsPanel />}
    </Box>
  );
};
